// Bottom-sheet shown when the user taps a non-media file in the
// browser. Shows a rich preview header (type-icon tile, filename,
// mime/size/modified metadata) above the standard file actions
// (download / rename / move / delete). Built on ItemActionSheet's
// header slot so action ordering stays consistent with the
// multi-select action sheet.

import { ItemActionSheet } from "./ItemActionSheet";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  fileName: string;
  mime?: string;
  sizeBytes?: number;
  modifiedEpochSeconds?: number;
  onExport: () => void;
  onRename: () => void;
  onMove: () => void;
  onDelete: () => void;
}

export function FileInfoSheet({
  open,
  onOpenChange,
  fileName,
  mime,
  sizeBytes,
  modifiedEpochSeconds,
  onExport,
  onRename,
  onMove,
  onDelete,
}: Props) {
  const header = (
    <FileInfoHeader
      fileName={fileName}
      mime={mime}
      sizeBytes={sizeBytes}
      modifiedEpochSeconds={modifiedEpochSeconds}
    />
  );

  return (
    <ItemActionSheet
      open={open}
      onOpenChange={onOpenChange}
      title={fileName}
      single
      header={header}
      onExport={onExport}
      onRename={onRename}
      onMove={onMove}
      onDelete={onDelete}
    />
  );
}

function FileInfoHeader({
  fileName,
  mime,
  sizeBytes,
  modifiedEpochSeconds,
}: {
  fileName: string;
  mime?: string;
  sizeBytes?: number;
  modifiedEpochSeconds?: number;
}) {
  const kind = kindLabel(fileName, mime);
  const size = sizeBytes !== undefined ? humanBytes(sizeBytes) : null;
  const modified =
    modifiedEpochSeconds !== undefined
      ? humanDate(modifiedEpochSeconds)
      : null;
  const parts = [kind, size, modified].filter(Boolean).join(" · ");

  return (
    <div className="flex items-center gap-3">
      <div
        className={cn(
          "flex h-12 w-12 items-center justify-center rounded-lg font-mono text-[11px] font-bold uppercase text-white",
          kindBgClass(mime),
        )}
      >
        {extLabel(fileName)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-text">
          {fileName}
        </div>
        <div className="mt-0.5 text-xs text-text-dim">{parts}</div>
      </div>
    </div>
  );
}

function extLabel(fileName: string): string {
  const i = fileName.lastIndexOf(".");
  return i >= 0 ? fileName.slice(i + 1).slice(0, 4).toUpperCase() : "FILE";
}

function kindLabel(fileName: string, mime?: string): string {
  if (mime === "application/pdf") return "PDF document";
  if (mime?.startsWith("image/")) return "Image";
  if (mime?.startsWith("video/")) return "Video";
  if (mime?.startsWith("audio/")) return "Audio";
  if (mime?.startsWith("text/")) return "Text";
  if (
    mime === "application/zip" ||
    mime === "application/x-tar" ||
    mime === "application/x-7z-compressed"
  ) {
    return "Archive";
  }
  const ext = fileName.lastIndexOf(".");
  return ext >= 0 ? `${fileName.slice(ext + 1).toUpperCase()} file` : "File";
}

function kindBgClass(mime?: string): string {
  if (mime === "application/pdf") return "bg-red-600";
  if (mime?.startsWith("image/")) return "bg-emerald-600";
  if (mime?.startsWith("video/")) return "bg-indigo-600";
  if (mime?.startsWith("audio/")) return "bg-purple-600";
  if (mime?.startsWith("text/")) return "bg-slate-600";
  if (
    mime === "application/zip" ||
    mime === "application/x-tar" ||
    mime === "application/x-7z-compressed"
  ) {
    return "bg-amber-600";
  }
  return "bg-zinc-600";
}

function humanBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GiB`;
}

function humanDate(epochSeconds: number): string {
  const date = new Date(epochSeconds * 1000);
  const now = new Date();
  const sameYear = date.getFullYear() === now.getFullYear();
  const fmt: Intl.DateTimeFormatOptions = sameYear
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" };
  return date.toLocaleDateString(undefined, fmt);
}
